import * as THREE from 'three';

export class PerformanceMonitor {
    constructor(app) {
        this.app = app;
        this.frameCount = 0;
        this.lastTime = performance.now();
        this.fps = 60;
        this.averageFps = 60;
        this.fpsHistory = [];
        this.maxHistoryLength = 60;
        
        this.isLowPerformance = false;
        this.adaptiveQuality = true;
        this.qualityLevel = 1; // 0 = low, 1 = medium, 2 = high
        
        this.thresholds = {
            low: 30,
            medium: 45,
            high: 55
        };
        
        this.init();
    }

    init() {
        this.createFpsDisplay();
        this.startMonitoring();
    }

    createFpsDisplay() {
        // Disabled FPS display as requested
        if (false && !this.app.deviceDetector.isMobile && import.meta.env.DEV) {
            this.fpsDisplay = document.createElement('div');
            this.fpsDisplay.style.cssText = `
                position: fixed;
                top: 10px;
                left: 10px;
                background: rgba(0, 0, 0, 0.8);
                color: #00d4ff;
                padding: 8px;
                font-family: monospace;
                font-size: 12px;
                border-radius: 4px;
                z-index: 1000;
                pointer-events: none;
            `;
            document.body.appendChild(this.fpsDisplay);
        }
    }

    startMonitoring() {
        setInterval(() => {
            this.updateFps();
            this.checkPerformance();
        }, 1000);
    }

    updateFps() {
        this.fpsHistory.push(this.fps);
        if (this.fpsHistory.length > this.maxHistoryLength) {
            this.fpsHistory.shift();
        }
        
        this.averageFps = this.fpsHistory.reduce((sum, fps) => sum + fps, 0) / this.fpsHistory.length;
        
        if (this.fpsDisplay) {
            this.fpsDisplay.textContent = `FPS: ${Math.round(this.fps)} | Avg: ${Math.round(this.averageFps)} | Quality: ${this.getQualityName()}`;
        }
    }

    checkPerformance() {
        if (!this.adaptiveQuality) return;
        
        const currentQuality = this.qualityLevel;
        
        if (this.averageFps < this.thresholds.low && this.qualityLevel > 0) {
            this.qualityLevel = 0;
            this.adjustQuality();
        } else if (this.averageFps < this.thresholds.medium && this.qualityLevel > 1) {
            this.qualityLevel = 1;
            this.adjustQuality();
        } else if (this.averageFps > this.thresholds.high && this.qualityLevel < 2) {
            this.qualityLevel = Math.min(2, this.qualityLevel + 1);
            this.adjustQuality();
        }
        
        if (currentQuality !== this.qualityLevel) {
            console.log(`Performance adjusted to: ${this.getQualityName()}`);
        }
    }

    adjustQuality() {
        const renderer = this.app.renderer;
        const scenes = this.app.scenes;
        
        switch (this.qualityLevel) {
            case 0: // Low quality
                renderer.setPixelRatio(0.5);
                renderer.shadowMap.enabled = false;
                this.reduceLighting();
                this.disableParticles();
                break;
                
            case 1: // Medium quality
                renderer.setPixelRatio(Math.min(window.devicePixelRatio * 0.75, 1.5));
                renderer.shadowMap.enabled = true;
                renderer.shadowMap.type = THREE.BasicShadowMap;
                this.reduceLighting();
                break;
                
            case 2: // High quality
                renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
                renderer.shadowMap.enabled = true;
                renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                this.restoreLighting();
                break;
        }
    }

    reduceLighting() {
        if (this.app.sceneManager.currentScene) {
            this.app.sceneManager.currentScene.traverse((object) => {
                if (object.isLight && object.type !== 'AmbientLight') {
                    object.intensity *= 0.7;
                }
            });
        }
    }

    restoreLighting() {
        if (this.app.sceneManager.currentScene) {
            this.app.sceneManager.currentScene.traverse((object) => {
                if (object.isLight && object.type !== 'AmbientLight') {
                    object.intensity /= 0.7;
                }
            });
        }
    }

    disableParticles() {
        // Disable particle systems in scenes if they exist
        Object.values(this.app.scenes).forEach(scene => {
            if (scene.particles) {
                scene.particles.visible = false;
            }
        });
    }

    getQualityName() {
        switch (this.qualityLevel) {
            case 0: return 'Low';
            case 1: return 'Medium';
            case 2: return 'High';
            default: return 'Unknown';
        }
    }

    update() {
        this.frameCount++;
        const currentTime = performance.now();
        const deltaTime = currentTime - this.lastTime;
        
        if (deltaTime >= 1000) {
            this.fps = Math.round((this.frameCount * 1000) / deltaTime);
            this.frameCount = 0;
            this.lastTime = currentTime;
        }
    }

    setAdaptiveQuality(enabled) {
        this.adaptiveQuality = enabled;
    }

    forceQuality(level) {
        this.qualityLevel = Math.max(0, Math.min(2, level));
        this.adjustQuality();
    }

    dispose() {
        if (this.fpsDisplay) {
            document.body.removeChild(this.fpsDisplay);
        }
    }
}